import React, { useState } from 'react';
import type User from '@this/domain/user/user';
import { headerHeight } from '@this/shared/header/header.style';
import { createSemanticColors } from '@this/shared/ui/theme/tokens/semantic-tokens';
import { TabiregiProfilePopover } from './tabiregi_profile_popover';

const colors = createSemanticColors(1);
const logoPath = '/images/logo/aitravel/logo_AIT_White-Color.png';

type Props = {
  user?: User;
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    width: '100%',
    height: headerHeight
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    height: headerHeight,
    background: colors.background.tertiary,
    borderBottom: `1px solid ${colors.brand.primary}`,
    paddingRight: 10
  },
  logo: {
    display: 'block',
    width: 173,
    height: 28,
    marginLeft: 10,
    objectFit: 'contain'
  },
  accountArea: {
    position: 'relative',
    marginLeft: 'auto'
  },
  accountTrigger: {
    display: 'flex',
    alignItems: 'center',
    color: colors.common.white,
    fontSize: 11,
    lineHeight: '15px',
    padding: '8px 8px 8px 16px',
    cursor: 'pointer'
  },
  accountTexts: {
    maxWidth: 160
  },
  accountPrimary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  accountSecondary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  triangle: {
    fontSize: 7,
    marginLeft: 8
  },
  popover: {
    position: 'absolute',
    right: 0,
    top: 50,
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.2)',
    width: 250,
    borderRadius: '0 0 12px 12px',
    zIndex: 1500
  }
};

const truncate = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength)}...`;
  }
  return text;
};

export const TabiregiHeader: React.FC<Props> = ({ user }) => {
  const [showAccount, setShowAccount] = useState(false);
  const [hovered, setHovered] = useState(false);
  const showAccountTrigger = Boolean(user);

  const primaryText = user?.organization?.name ? truncate(user.organization.name, 11) : '';
  const secondaryText = user?.name ? truncate(user.name, 11) : '';

  return (
    <header style={styles.header} data-testid="tabiregi-header">
      <div style={styles.inner}>
        <img src={logoPath} alt="AI Travel" style={styles.logo} />
        {showAccountTrigger && (
          <div style={styles.accountArea}>
            <div
              style={
                hovered
                  ? { ...styles.accountTrigger, background: 'rgba(255, 255, 255, 0.15)' }
                  : styles.accountTrigger
              }
              onClick={() => setShowAccount(!showAccount)}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              data-testid="tabiregi-header-account-trigger"
            >
              <div style={styles.accountTexts}>
                {primaryText && <div style={styles.accountPrimary}>{primaryText}</div>}
                {secondaryText && <div style={styles.accountSecondary}>{secondaryText}</div>}
              </div>
              <div style={styles.triangle}>▼</div>
            </div>
            {showAccount && <div style={styles.popover}>{user && <TabiregiProfilePopover user={user} />}</div>}
          </div>
        )}
      </div>
    </header>
  );
};
